import AddIcon from '@mui/icons-material/Add';
import {
    Box,
    Divider,
    Fab,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    TextField,
    Tooltip
} from '@mui/material';
import { SyntheticEvent, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { If } from '../../common/IfComponent';
import { AgreementStatus, EntityTypes } from '../../common/PricePrgClient';
import { StyledTabs } from '../../common/StyledComponents';
import { scrollableY } from '../../common/theme/scrollbar';
import { RootState } from '../../store';
import AgreementDetails from './AgreementDetails';
import AgreementList from './AgreementList';
import { AgreementPrices } from './AgreementPrices';
import CreateAgreementDialog from './CreateAgreementDialog';
import { getSelectedAgreementId } from './redux/agreementSelectors';
import { setAgreementStatusFilter, showCreateDialog } from './redux/agreementSlices';

const mapStateToProps = (state: RootState) => ({
    selectedAgreementId: getSelectedAgreementId(state),
    agreementUi: state.agreement.ui,
    appRestrictions: state.settings.applicationRestrictions
});

type Props = ReturnType<typeof mapStateToProps>;

const Agreement = ({
    selectedAgreementId,
    agreementUi: { isShowingCreateDialog, statusFilter },
    appRestrictions
}: Props) => {
    const [tabIndex, setTabIndex] = useState(0);
    const [searchFilter, setSearchFilter] = useState('');
    const dispatch = useDispatch();

    const handleTabChange = (event: SyntheticEvent<Element, Event>, value: number) => {
        setTabIndex(value);
    };

    const createAgreement = () => {
        dispatch(showCreateDialog(true));
    };

    const handleSetAgreementStatusFilter = (status: AgreementStatus | -1) => {
        dispatch(setAgreementStatusFilter(status === -1 ? null : status));
    };
    const isCreateDisabled = useMemo(() => {
        const key = EntityTypes.Agreement.toString();
        return appRestrictions && (appRestrictions[key]?.isCreateDisabled ?? false);
    }, [appRestrictions]);
    return (
        <Grid container sx={{ flexFlow: 'row', paddingLeft: '0px' }}>
            <Grid item xs={3} sx={theme => ({ ...scrollableY(theme), paddingRight: theme.spacing(2) })}>
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    sx={theme => ({ marginBottom: theme.spacing(2) })}
                >
                    <Grid item xs={3}>
                        <FormControl fullWidth variant="standard">
                            <InputLabel id="agreementStatusFilter">Status filter</InputLabel>
                            <Select
                                defaultValue={-1}
                                label="agreementStatusFilter"
                                onChange={e =>
                                    handleSetAgreementStatusFilter(e.target.value as AgreementStatus | -1)
                                }
                                value={statusFilter ?? -1}
                            >
                                <MenuItem value={-1}>All</MenuItem>
                                {[
                                    AgreementStatus.Inactive,
                                    AgreementStatus.New,
                                    AgreementStatus.Published
                                ].map(a => (
                                    <MenuItem key={a} value={a}>
                                        {AgreementStatus[a]}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            sx={{ mt: 1 }}
                            size="small"
                            label="Filter"
                            defaultValue={searchFilter}
                            onChange={e => setSearchFilter(e.currentTarget.value)}
                        />
                    </Grid>
                    <Grid item>
                        <Tooltip title="Create new agreement">
                            <span>
                                <Fab
                                    disabled={isCreateDisabled}
                                    size="small"
                                    color="primary"
                                    onClick={createAgreement}
                                >
                                    <AddIcon />
                                </Fab>
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Box sx={{ height: '82vh' }}>
                    <AgreementList searchFilter={searchFilter} />
                </Box>
            </Grid>
            <Divider orientation="vertical" flexItem />
            <Grid item xs={9}>
                <If
                    condition={selectedAgreementId != null}
                    then={
                        <>
                            <StyledTabs
                                value={tabIndex}
                                onChange={handleTabChange}
                                indicatorColor="primary"
                                textColor="primary"
                            >
                                <Tab label="Details" />
                                <Tab label="Prices" />
                            </StyledTabs>

                            <Box p={2}>
                                {tabIndex === 0 && <AgreementDetails />}

                                {tabIndex === 1 && <AgreementPrices />}
                            </Box>
                        </>
                    }
                    else={null}
                />
            </Grid>
            {isShowingCreateDialog && (
                <CreateAgreementDialog closeDialog={() => dispatch(showCreateDialog(false))} />
            )}
        </Grid>
    );
};

export default connect(mapStateToProps)(Agreement);
